import React, { createContext, useContext, useMemo, useState } from 'react';
import { APP_MODAL_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import { EditNoteActionSheet } from '@/components/order-guide/edit-note-action-sheet';
import { EditNoteFormValuesV2 } from '@/components/order-guide/edit-note-content-v2';
import { EssentialItemActionSheet } from '@/components/order-guide/essential-item-action-sheet';
import { ItemListOptionMetadata } from './item-context-types';
import {
  ItemsContext_OrderGuideItemFieldsFragment,
  UpdateOrderGuideItemDispositionsItemInput,
} from '@/types';
import { NavigationProp } from '@react-navigation/native';
import { NoteType } from '@/enums';
import { useGetOrderGuideItem } from '@/hooks/use-get-order-guide-item';
import { useMultiOrderGuideMutations } from '@/hooks/use-multi-order-guide-mutations';
import { useOrderGuideItemsContext } from './order-guide-items-context';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useToasts } from './toast-context';
import { useVariantPackInfoContext } from './variant-pack-info-context';

export interface UpdateOrderGuideItemContextType {
  editEssentialItem: (
    itemMetadata: ItemListOptionMetadata,
    variantPackUUID?: string,
  ) => void;
  editItemNotes: (
    itemMetadata: ItemListOptionMetadata,
    type?: NoteType,
  ) => void;
  isItemMinimized: (itemMetadata: ItemListOptionMetadata) => boolean;
  isRowEssential: (
    itemMetadata: ItemListOptionMetadata,
    variantPackUUID?: string,
  ) => boolean;
  updateDispositions: (
    itemMetadata: ItemListOptionMetadata,
    nextValue: boolean,
  ) => Promise<void>;
}

export const UpdateOrderGuideItemContext =
  createContext<UpdateOrderGuideItemContextType | null>(null);

enum EditAction {
  ESSENTIAL_ITEM = 'ESSENTIAL_ITEM',
  NOTE = 'NOTE',
}

type EditableItem =
  | {
      action: EditAction.ESSENTIAL_ITEM;
      itemMetadata: ItemListOptionMetadata;
    }
  | {
      action: EditAction.NOTE;
      itemMetadata: ItemListOptionMetadata;
      type?: NoteType;
    };

interface Props {
  children: React.ReactNode;
  navigation?: NavigationProp<AppModalStackParams>;
}

export const UpdateOrderGuideItemContextProvider: React.FC<Props> = ({
  children,
  navigation,
}) => {
  const getOrderGuideItem = useGetOrderGuideItem();
  const { updateItemDispositions, updateItemNotes } =
    useMultiOrderGuideMutations();
  const { showErrorToast } = useToasts();
  const { essentialVariantPackUUIDs } = useVariantPackInfoContext();
  const isLineItemNoteEnabled = useSupplierDynamicConfigValue(
    'line_item_notes_config',
  )?.enabled;

  const { collections } = useOrderGuideItemsContext();

  const [minimizedMap, setMinimizedMap] = useState<Record<string, boolean>>({});
  const [editableItem, setEditableItem] = useState<EditableItem | null>(null);

  const value = useMemo<UpdateOrderGuideItemContextType>(() => {
    return {
      editEssentialItem: (
        itemMetadata: ItemListOptionMetadata,
        variantPackUUID?: string,
      ) => {
        let editableMetadata = itemMetadata;
        if (variantPackUUID) {
          const foundSku = itemMetadata.skus.find(
            (sku) => sku.variantPackUUID === variantPackUUID,
          );
          if (foundSku) {
            editableMetadata = foundSku;
          }
        }
        setEditableItem({
          action: EditAction.ESSENTIAL_ITEM,
          itemMetadata: editableMetadata,
        });
      },
      editItemNotes: (
        itemMetadata: ItemListOptionMetadata,
        type?: NoteType,
      ) => {
        if (isLineItemNoteEnabled && navigation) {
          navigation?.navigate(APP_MODAL_ROUTES.EditNote, {
            collections,
            getOrderGuideItem,
            itemMetadata,
            onSave: async (
              orderGuideItem: ItemsContext_OrderGuideItemFieldsFragment,
              values: EditNoteFormValuesV2,
            ) => {
              const partitionUUID = itemMetadata.orderGuidePartitionUUID;
              if (!orderGuideItem.variant_pack || !partitionUUID) {
                return;
              }

              const initialMemoValue =
                orderGuideItem.order_guide_restaurant?.memo ?? '';
              const initialMemoDeleteAfterNextOrder =
                !orderGuideItem.order_guide_restaurant?.persist_memo;
              if (
                values.memoValue !== initialMemoValue ||
                values.memoDeleteAfterNextOrder !==
                  initialMemoDeleteAfterNextOrder
              ) {
                const saved = await updateItemNotes({
                  isRequest: false,
                  notes: values.memoValue || null,
                  orderGuideItemUUID: orderGuideItem.order_guide_item_id,
                  partitionUUID,
                  persistNotes: !values.memoDeleteAfterNextOrder,
                  variantPackUUID: orderGuideItem.variant_pack.uuid,
                });

                // Early return if the memo couldn't be saved
                if (!saved) {
                  return;
                }
              }

              const initialRequestValue =
                orderGuideItem.order_guide_restaurant?.request ?? '';
              const initialRequestDeleteAfterNextOrder =
                !orderGuideItem.order_guide_restaurant?.persist_request;
              if (
                values.requestValue !== initialRequestValue ||
                values.requestDeleteAfterNextOrder !==
                  initialRequestDeleteAfterNextOrder
              ) {
                await updateItemNotes({
                  isRequest: true,
                  notes: values.requestValue || null,
                  orderGuideItemUUID: orderGuideItem.order_guide_item_id,
                  partitionUUID,
                  persistNotes: !values.requestDeleteAfterNextOrder,
                  variantPackUUID: orderGuideItem.variant_pack.uuid,
                });
              }
            },
          });
        } else {
          setEditableItem({
            action: EditAction.NOTE,
            itemMetadata,
            type,
          });
        }
      },
      isItemMinimized: (itemMetadata: ItemListOptionMetadata) => {
        const orderGuideItem = getOrderGuideItem(itemMetadata);
        if (orderGuideItem) {
          const fallbackValue = orderGuideItem.order_guide_restaurant
            ? orderGuideItem.order_guide_restaurant.is_minimized
            : false;
          return (
            minimizedMap[orderGuideItem.order_guide_item_id] ?? fallbackValue
          );
        }
        return false;
      },
      isRowEssential: (
        itemMetadata: ItemListOptionMetadata,
        variantPackUUID?: string,
      ) => {
        if (variantPackUUID) {
          return essentialVariantPackUUIDs.has(variantPackUUID);
        }
        if (itemMetadata.skus.length <= 1) {
          return essentialVariantPackUUIDs.has(itemMetadata.variantPackUUID);
        }
        return itemMetadata.skus.some((sku) => {
          return essentialVariantPackUUIDs.has(sku.variantPackUUID);
        });
      },
      updateDispositions: async (
        itemMetadata: ItemListOptionMetadata,
        nextValue: boolean,
      ) => {
        const orderGuideItemUUIDs = itemMetadata.skus.reduce<string[]>(
          (acc, sku) => {
            const ogiUUID = getOrderGuideItem(sku)?.order_guide_item_id;
            if (ogiUUID) {
              acc.push(ogiUUID);
            }
            return acc;
          },
          [],
        );
        try {
          setMinimizedMap((prev) => {
            return orderGuideItemUUIDs.reduce((acc, uuid) => {
              return {
                ...acc,
                [uuid]: nextValue,
              };
            }, prev);
          });

          await updateItemDispositions(
            itemMetadata.skus.reduce<
              UpdateOrderGuideItemDispositionsItemInput[]
            >((acc, sku) => {
              const partitionUUID = sku.orderGuidePartitionUUID;
              const variantPackUUID = sku.variantPackUUID;
              if (partitionUUID && variantPackUUID) {
                acc.push({
                  is_minimized: nextValue,
                  partition_id: partitionUUID,
                  variant_pack_id: variantPackUUID,
                });
              }
              return acc;
            }, []),
          );
        } catch (e) {
          showErrorToast();
        } finally {
          setMinimizedMap((prev) => {
            return orderGuideItemUUIDs.reduce((acc, uuid) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { [uuid]: _, ...rest } = acc;
              return rest;
            }, prev);
          });
        }
      },
    };
  }, [
    essentialVariantPackUUIDs,
    getOrderGuideItem,
    isLineItemNoteEnabled,
    minimizedMap,
    navigation,
    showErrorToast,
    updateItemDispositions,
    updateItemNotes,
    collections,
  ]);

  return (
    <UpdateOrderGuideItemContext.Provider value={value}>
      {children}
      {editableItem && editableItem.action === EditAction.NOTE ? (
        <EditNoteActionSheet
          itemMetadata={editableItem.itemMetadata}
          onClose={() => {
            setEditableItem(null);
          }}
          onSave={async (orderGuideItem, nextValue, persistNotes) => {
            const partitionUUID =
              editableItem.itemMetadata.orderGuidePartitionUUID;
            if (!orderGuideItem.variant_pack || !partitionUUID) {
              return;
            }

            await updateItemNotes({
              notes: nextValue || null,
              orderGuideItemUUID: orderGuideItem.order_guide_item_id,
              partitionUUID,
              persistNotes,
              variantPackUUID: orderGuideItem.variant_pack.uuid,
            });
          }}
        />
      ) : null}

      {editableItem && editableItem.action === EditAction.ESSENTIAL_ITEM ? (
        <EssentialItemActionSheet
          itemMetadata={editableItem.itemMetadata}
          onClose={() => {
            setEditableItem(null);
          }}
        />
      ) : null}
    </UpdateOrderGuideItemContext.Provider>
  );
};

export const useUpdateOrderGuideItemContext = () => {
  const context = useContext(UpdateOrderGuideItemContext);
  if (!context) {
    throw new Error(
      'useUpdateOrderGuideItemContext must be used within a UpdateOrderGuideItemContextProvider',
    );
  }
  return context;
};
